import gsap from 'gsap';

/** Max extra rotation (deg) from horizontal pointer position when cursor is not over this polaroid */
const WW_SWAY_DEG_PASSIVE = 1.1;
/** Stronger max (deg) when the cursor is over this polaroid */
const WW_SWAY_DEG_HOVER = 5;

/**
 * Careers “What we offer”: idle swing stays in CSS; pointer adds horizontal rotation only,
 * around the pin (transform-origin in SCSS). GSAP smooths `--ww-sway-r`.
 */
class CareersWhatWeOffer extends HTMLElement {
  /** @type {HTMLElement[]} */
  #targets = [];
  /** @type {Map<HTMLElement, ReturnType<typeof gsap.quickTo>>} */
  #rTo = new Map();
  #listening = false;
  /** @type {((e: PointerEvent) => void) | null} */
  #boundMove = null;
  /** @type {(() => void) | null} */
  #boundLeave = null;

  #handlePointerMove = (event) => {
    if (!this.#listening) {
      return;
    }
    const px = event.clientX;
    const py = event.clientY;
    this.#targets.forEach((node) => {
      const rTo = this.#rTo.get(node);
      if (!rTo) {
        return;
      }
      const rect = node.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const nx = (px - cx) / Math.max(rect.width, 1);
      const clamped = Math.max(-1, Math.min(1, nx));
      const over = px >= rect.left && px <= rect.right && py >= rect.top && py <= rect.bottom;
      const cap = over ? WW_SWAY_DEG_HOVER : WW_SWAY_DEG_PASSIVE;
      rTo(clamped * cap);
    });
  };

  #resetPointerMotion = () => {
    this.#targets.forEach((node) => {
      gsap.to(node, {
        '--ww-sway-r': '0deg',
        duration: 0.65,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    });
  };

  #randomizePolaroidSwings() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.querySelectorAll('.careers_what_we_offer__polaroid-swing').forEach((el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }
      if (reduced) {
        el.style.animation = 'none';
        return;
      }
      const duration = 2.4 + Math.random() * 4.2;
      const delay = -Math.random() * duration;
      el.style.animationDuration = `${duration.toFixed(2)}s`;
      el.style.animationDelay = `${delay.toFixed(2)}s`;
    });
  }

  connectedCallback() {
    this.#randomizePolaroidSwings();
    this.#targets = Array.from(this.querySelectorAll('[data-careers-ww-sway]')).filter(
      (n) => n instanceof HTMLElement,
    );
    if (!this.#targets.length) {
      return;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    this.#targets.forEach((node) => {
      gsap.set(node, { '--ww-sway-r': '0deg' });
      const rTo = gsap.quickTo(node, '--ww-sway-r', {
        duration: 0.72,
        ease: 'power2.out',
        units: 'deg',
      });
      this.#rTo.set(node, rTo);
    });

    this.#listening = true;
    this.#boundMove = this.#handlePointerMove.bind(this);
    this.#boundLeave = () => this.#resetPointerMotion();
    window.addEventListener('pointermove', this.#boundMove, { passive: true });
    this.addEventListener('pointerleave', this.#boundLeave);
    document.documentElement.addEventListener('mouseleave', this.#boundLeave);
    window.addEventListener('blur', this.#boundLeave);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  #onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      this.#resetPointerMotion();
    }
  };

  disconnectedCallback() {
    this.#listening = false;
    if (this.#boundMove) {
      window.removeEventListener('pointermove', this.#boundMove);
    }
    if (this.#boundLeave) {
      this.removeEventListener('pointerleave', this.#boundLeave);
      document.documentElement.removeEventListener('mouseleave', this.#boundLeave);
      window.removeEventListener('blur', this.#boundLeave);
    }
    document.removeEventListener('visibilitychange', this.#onVisibility);
    this.#targets.forEach((node) => {
      gsap.killTweensOf(node);
    });
    this.#rTo.clear();
    this.#targets = [];
    this.#boundMove = null;
    this.#boundLeave = null;
  }
}

if (!customElements.get('careers-what-we-offer')) {
  customElements.define('careers-what-we-offer', CareersWhatWeOffer);
}
